import { LockOutlined, WarningOutlined } from '@ant-design/icons';
import { useGetBackProcedimentiIdEndoprocedimenti } from '@framework/api-client/api/backoffice/procedimenti/procedimenti.ts';
import { type ComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneDTO.ts';
import { type DestinatarioDTO } from '@framework/api-client/api/inquiries-compile/schemas/destinatarioDTO.ts';
import { EnumTipoDestinatarioDTOCodice } from '@framework/api-client/api/inquiries-compile/schemas/enumTipoDestinatarioDTOCodice.ts';
import { type TipoComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoComunicazioneDTO.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { useField } from '@framework/form/field/useField.tsx';
import {
  SelectInput,
  type SelectOption,
} from '@framework/form/inputs/SelectInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Alert, Input, Space, Typography } from 'antd';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCommunicationsContext } from '../../context/CommunicationsContext.tsx';

interface ICommunicationReceiverInputOptions extends SelectOption {
  /**
   * Mi costruisco subito l'oggetto da salvare nel form
   * per evitare di dover fare il mapping da value a DestinatarioDTO
   * ogni volta
   */
  toSave: DestinatarioDTO;
}

interface ICommunicationReceiverInputProps {
  communicationType: TipoComunicazioneDTO | null | undefined;
  communication: ComunicazioneDTO | null | undefined;
  isManual: boolean | undefined;
}

function getReceiversDefault(
  communicationType: TipoComunicazioneDTO,
): DestinatarioDTO[] {
  if (!communicationType.tipoComunicazioneDestinatario) return [];
  return (
    communicationType.tipoComunicazioneDestinatario
      // Di default inseriamo tutti i destinatari tranne gli endoprocedimenti
      .filter(
        to =>
          to.tipoDestinatario?.codice !==
          EnumTipoDestinatarioDTOCodice.ENDOPROCEDIMENTI_PRIMO_LIVELLO,
      )
      .map(to => ({
        // Il codice deve esserci sicuramente, usiamo as any per forzare il cast
        tipoDestinatario: to.tipoDestinatario?.codice as any,
      }))
  );
}

/**
 * Select per la selezione dei destinatari della comunicazione.
 * Gestisce internamente il valore di default e il warning per la mancanza di endoprocedimenti.
 */
export function CommunicationReceiverInput(
  props: ICommunicationReceiverInputProps,
) {
  const { communicationType, communication, isManual } = props;

  const { t } = useTranslation();

  const { procedureId } = useCommunicationsContext();

  const editable = !!communicationType?.flDestinatariEditabili || isManual;

  // Controllo che ci siano endoprocedimenti come destinatari
  const hasSubprocedure =
    communicationType?.tipoComunicazioneDestinatario?.some(
      r =>
        r.tipoDestinatario?.codice ===
        EnumTipoDestinatarioDTOCodice.ENDOPROCEDIMENTI_PRIMO_LIVELLO,
    );

  // Recupero la lista degli endoprocedimenti (se sono BO e sono necessari)
  const {
    data: subproceduresData,
    isLoading,
    error,
  } = useGetBackProcedimentiIdEndoprocedimenti(procedureId!, {
    query: { enabled: procedureId != null && hasSubprocedure },
  });

  const subprocedures = useMemo(
    () => subproceduresData?.data ?? [],
    [subproceduresData?.data],
  );

  // Popolo i possibili endoprocedimenti come destinatari
  const subprocedureOptions: ICommunicationReceiverInputOptions[] =
    useMemo(() => {
      if (!hasSubprocedure) {
        return [];
      }
      if (!subprocedures) {
        return [];
      }

      return subprocedures.map(subprocedure => ({
        label: `${subprocedure.tipologiaProcedimento?.nomeTipo ?? 'N/A'} N.${
          subprocedure.numero ?? ''
        } (${subprocedure.nomeUfficioAvvio ?? 'N/A'})`,
        value: `${subprocedure.id}`,
        disabled: false,
        toSave: {
          tipoDestinatario:
            EnumTipoDestinatarioDTOCodice.ENDOPROCEDIMENTI_PRIMO_LIVELLO,
          idEndoprocedimento: subprocedure.id ?? '<unknown>',
        } satisfies DestinatarioDTO,
      }));
    }, [hasSubprocedure, subprocedures]);

  // Opzioni per il select
  const options: ICommunicationReceiverInputOptions[] = useMemo(() => {
    const common =
      communicationType?.tipoComunicazioneDestinatario
        // Gestiamo gli endoprocedimenti con una logica custom
        ?.filter(
          to =>
            to.tipoDestinatario?.codice !==
            EnumTipoDestinatarioDTOCodice.ENDOPROCEDIMENTI_PRIMO_LIVELLO,
        )
        .map(to => {
          // TODO: mostrare la denominazione della competenza secondaria // NOSONAR
          // per UFFICIO_COMPETENZA_SECONDARIA, quando il backend esporrà
          // to.tipoDestinatario.tipoCompetenzaSecondaria
          const label = to.tipoDestinatario?.denominazione ?? 'N/A';

          return {
            label: (
              <Space>
                {!editable ? <LockOutlined /> : null}
                {label}
              </Space>
            ),
            value: `${to.tipoDestinatario?.codice}`,
            disabled: !editable,
            toSave: {
              tipoDestinatario: to.tipoDestinatario?.codice,
            } satisfies DestinatarioDTO,
          };
        }) ?? [];

    return (
      [...common, ...subprocedureOptions]
        // Ordino i destinatari
        .sort((a, b) => {
          if (a.disabled !== b.disabled) {
            return a.disabled ? 1 : -1; // i non-disabilitati prima
          }

          if (typeof a.label === 'string' && typeof b.label === 'string') {
            return a.label.localeCompare(b.label); // ordine alfabetico
          }
          return 0;
        })
    );
  }, [
    communicationType?.tipoComunicazioneDestinatario,
    editable,
    subprocedureOptions,
  ]);

  const { setValue: setReceivers, getValue: getReceivers } =
    useField<DestinatarioDTO[]>('destinatari');

  const receivers = useWatch({ name: 'destinatari' }) as DestinatarioDTO[];

  /**
   * Effect che imposta i destinatari di default.
   */
  useEffect(() => {
    if (isLoading || error) {
      return;
    }

    if (!communicationType) {
      return;
    }

    if (!communication) {
      return;
    }

    // SE è già stata salvata, non facciamo nulla
    if (communication.id) {
      return;
    }

    // SE è nuova e non ci sono receiver impostati, li mettiamo tutti
    const receivers = getReceivers();
    if (!receivers || receivers.length === 0) {
      const defaultReceivers = getReceiversDefault(communicationType);
      if (defaultReceivers.length > 0) {
        setReceivers(defaultReceivers);
      }
    }
  }, [
    communication,
    communicationType,
    error,
    getReceivers,
    isLoading,
    setReceivers,
    subprocedures,
  ]);

  // Se true mostra il warning che richiede l'inserimento di un endoprocedimento
  // "Se i destinatari non sono modificabili e gli endoprocedimenti sono tra i destinatari e non ci sono endoprocedimenti selezionati"
  const showWarning =
    !editable &&
    hasSubprocedure &&
    subprocedures.length > 0 &&
    !receivers.some(
      r =>
        r.tipoDestinatario ===
        EnumTipoDestinatarioDTOCodice.ENDOPROCEDIMENTI_PRIMO_LIVELLO,
    );

  if (!communicationType) {
    return null;
  }

  if (error) {
    return (
      <Alert
        message={getNetworkErrorMessage(
          error,
          t(
            'communication-receiver-input.error-loading-subprocedures',
            'Errore durante il caricamento degli endoprocedimenti',
          ),
        )}
        type="error"
      />
    );
  }

  if (isLoading) {
    return <Input placeholder={t('loading', 'Caricamento...')} readOnly />;
  }

  return (
    <FormItem
      name={'destinatari'}
      label={t('communications.receiver-label', 'Destinatari')}
      layout={{ removeHelpMargin: true }}
      help={
        showWarning ? (
          <Typography.Text type="warning">
            <Space>
              <WarningOutlined />
              {t(
                'communication-receiver-input.receiver-description',
                'Includere almeno un ente terzo tra i destinatari',
              )}
            </Space>
          </Typography.Text>
        ) : undefined
      }
    >
      <SelectInput
        name={'destinatari'}
        size="large"
        mode="multiple"
        allowClear={!!editable}
        style={{ width: '100%' }}
        options={options}
        transformValue={(values: DestinatarioDTO[]) =>
          values.map(v => v.idEndoprocedimento ?? v.tipoDestinatario)
        }
        onChange={(values: string[], _, onChange) => {
          const newReceivers = values
            .map(value => options.find(o => o.value === value)?.toSave)
            .filter(Boolean) as DestinatarioDTO[];

          onChange(newReceivers);
        }}
        keepDisabledOptions
      />
    </FormItem>
  );
}

/**
 *  <>
      <Select
        // utilizzare readOnly per evitare la modifica del campo
        size="large"
        mode="multiple"
        allowClear={!!editable}
        style={{ width: '100%' }}
        options={options}
        value={receivers.map(r => r.idEndoprocedimento ?? r.tipoDestinatario)}
        // Gestisco l'onchange
        onChange={values => {
          const newReceivers = values
            .map(value => options.find(o => o.value === value)?.toSave)
            .filter(Boolean) as DestinatarioDTO[];
          setReceivers(newReceivers);
        }}
      />
      {showWarning && (
        <Typography.Text type="warning">
          <Space>
            <WarningOutlined />
            {t(
              'communication-receiver-input.receiver-description',
              'Includere almeno un ente terzo tra i destinatari',
            )}
          </Space>
        </Typography.Text>
      )}
    </>
 */
